import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent, VariantSelectedEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (
      this.closest('#MainContent[data-template="product.new-product-design"]') &&
      this.refs.addToCartButton.dataset.variantInvalid === 'true'
    ) return;

    // Skip animations when required add-ons are not satisfied
    if (!this.#areAddonsValid()) return;

    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  /**
   * Checks whether all required add-ons in the same section are satisfied.
   * Returns true when there is no addon component or all required add-ons are selected.
   * @returns {boolean}
   */
  #areAddonsValid() {
    const sectionEl = this.closest('.shopify-section, dialog');
    if (!sectionEl) return true;

    const addonEl = sectionEl.querySelector('product-addons-component');
    if (!addonEl || !('validate' in addonEl)) return true;

    // Peek at required add-ons without toggling error UI — just check state
    let valid = true;
    addonEl.querySelectorAll('.product-addons__item[data-required="true"]').forEach((el) => {
      const checkbox = el.querySelector('.product-addons__checkbox');
      const select = el.querySelector('.product-addons__select');

      if (checkbox && !/** @type {HTMLInputElement} */ (checkbox).checked) valid = false;
      else if (select && /** @type {HTMLSelectElement} */ (select).value === '') valid = false;
    });

    return valid;
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener('variant:validation', this.#onVariantValidation, { signal });

    // Initial validation check
    this.#checkInitialVariantValidity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const atcButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;

    // If the button is truly disabled (sold out / unavailable), early return
    if (atcButton?.getAttribute('disabled') === 'true' && atcButton?.getAttribute('data-variant-invalid') !== 'true') {
      return;
    }

    // Validate required variant options before proceeding
    if (!this.#validateVariantOptions()) {
      atcButton?.classList.remove('atc-added');
      return;
    }

    // Validate required add-ons before proceeding
    if (!this.#validateAddons()) {
      // Cancel the add-to-cart animation that was triggered by the preceding click event
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.classList.remove('atc-added');
      return;
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // Attach add-on fee payload if the product has add-ons selected
    const addonPayload = this.#getAddonPayload(formData.get('id'));
    if (addonPayload.length > 0) {
      formData.append('properties[_addon_rule_payload]', JSON.stringify(addonPayload));
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Reads the add-on selections from the product-addons-component in the same section.
   * Returns the addon payload array to be stored as a line item property.
   *
   * @param {FormDataEntryValue|null} variantId - The currently selected variant ID
   * @returns {Array<{rule_code: string, fee_variant_id: number, fee_price: number, scope: string, addon_key: string}>}
   */
  #getAddonPayload(variantId) {
    const sectionEl = this.closest('.shopify-section, dialog');
    if (!sectionEl) return [];

    const addonEl = sectionEl.querySelector('product-addons-component');
    if (!addonEl || !('getAddonPayload' in addonEl)) return [];

    // @ts-ignore — ProductAddonsComponent exposes getAddonPayload
    return addonEl.getAddonPayload(this.dataset.productId || '', String(variantId || ''));
  }

  /**
   * Validates required add-ons via the product-addons-component.
   * When validation fails, scrolls the first invalid add-on into view.
   *
   * @returns {boolean} true when valid (or no addon component present)
   */
  #validateAddons() {
    const sectionEl = this.closest('.shopify-section, dialog');
    if (!sectionEl) return true;

    const addonEl = sectionEl.querySelector('product-addons-component');
    if (!addonEl || !('validate' in addonEl)) return true;

    // @ts-ignore — ProductAddonsComponent exposes validate
    const isValid = addonEl.validate();

    if (!isValid) {
      const firstError = addonEl.querySelector('.product-addons__item--error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return isValid;
  }

  /**
   * Validates that all required variant options are selected.
   * Triggers validation display on the variant picker if invalid.
   *
   * @returns {boolean} true when all required options are selected
   */
  #validateVariantOptions() {
    const sectionEl = this.closest('.shopify-section, dialog');
    const variantPicker = /** @type {any} */ (sectionEl?.querySelector('variant-picker'));
    if (typeof variantPicker?.validateRequiredOptions !== 'function') return true;

    const result = variantPicker.validateRequiredOptions();
    if (!result.valid) {
      variantPicker.showErrorStates?.(result.unselectedPositions);
      return false;
    }
    return true;
  }

  /**
   * Immediately updates the hidden variant ID input when a new option is selected,
   * so that rapid add-to-cart clicks use the correct variant before the async fetch completes.
   * @param {VariantSelectedEvent} event
   */
  #onVariantSelected = (event) => {
    const { variantId } = event.detail.resource;
    this.refs.variantId.value = variantId ?? '';
  };

  /**
   * Handles variant validation events to update add-to-cart button state.
   * @param {Event & { detail?: { result?: { valid: boolean }}}} event
   */
  #onVariantValidation = (event) => {
    const result = /** @type {any} */ (event).detail?.result;
    if (result) this.#applyValidationResult(result.valid);
  };

  /**
   * Applies variant-validation state to the add-to-cart button: toggles
   * `data-variant-invalid`, `aria-disabled`, and swaps button text.
   * @param {boolean} valid - Whether all required options are selected
   */
  #applyValidationResult(valid) {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!button) return;

    if (valid) {
      button.removeAttribute('data-variant-invalid');
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('data-variant-invalid', 'true');
      button.setAttribute('aria-disabled', 'true');
    }

    const textContent = button.querySelector('.add-to-cart-text__content');
    if (!textContent) return;

    const translations = /** @type {any} */ (window).Theme?.translations;
    textContent.textContent = valid
      ? (translations?.actions?.add_to_cart || 'Add to cart')
      : (translations?.products?.product?.select_options || 'Select options');
  }

  /**
   * Performs initial validation check when component mounts.
   */
  #checkInitialVariantValidity() {
    const variantPicker = /** @type {any} */ (
      this.closest('.shopify-section, dialog')?.querySelector('variant-picker')
    );
    if (typeof variantPicker?.validateRequiredOptions !== 'function') return;

    const result = variantPicker.validateRequiredOptions();
    this.#applyValidationResult(result.valid);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.refs.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Capture variant-invalid state before morphing the button — the morph
    // replaces DOM content with the server version which doesn't know about
    // unselected options and would reset the text to "Add to cart".
    const wasVariantInvalid = currentAddToCartButton.getAttribute('data-variant-invalid') === 'true';

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Re-apply variant-invalid state after button morph so the text and
    // aria-disabled attribute are not clobbered by the server HTML.
    if (wasVariantInvalid) {
      this.#applyValidationResult(false);
    }

    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
